import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import DynamicForm from "@/common/DynamicForm";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import CustomModal from "../CustomModal";
import {
  getOptionsFromRefList,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
  hasCheckSecurity,
} from "@/util/common_function";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";

const BailRecordDetailForm = ({
  formData,
  form,
  startingPoint = "",
  // Reports the extra table's field keys/type map/current PK up to the
  // parent (ChargesBailRecordsDetails.jsx) whenever they change, since the
  // parent — not this form — is what merges the saved values into
  // bookingBails[].bookingBailsExtra[0] on save. See BookingBailsExtra
  // notes below.
  onExtraMetaChange,
}) => {
  const [bailDetail, setBailDetail] = useState(null);
  const { bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const fullSearch = useSelector((state) => state.fullSearch);

  // -----------------------------------------------------------------------
  // BookingBailsExtra — dynamic per-client fields. PCI shows these under
  // "Additional Bail Information", right before "Bail Administrative
  // Information" in BookingBailsInterface.asp — same spot below.
  // -----------------------------------------------------------------------
  const [bailExtraFields, setBailExtraFields] = useState([]);
  const [bailExtraFieldsReady, setBailExtraFieldsReady] = useState(false);
  const bailExtraTypeMapRef = useRef({});
  const [bailExtraFormValues, setBailExtraFormValues] = useState(null);
  const bailExtraIDRef = useRef(0);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/BookingBailsExtra`)
      .then((response) => {
        const tableData = response?.data?.filter((d) =>
          d?.columnName?.endsWith("_U"),
        );
        const converted = convertApiFields(tableData, "BookingBailsExtra");

        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        bailExtraTypeMapRef.current = typeMap;

        setBailExtraFields(converted);
        setBailExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  }, []);

  // Fetches the saved BookingBailsExtra row when editing an existing bail —
  // GET /api/extra/BookingBailsExtra/bookingBailID/{id}, same generic
  // endpoint BioExtra/ArrestReportsExtra/InmatePropertyExtra/ChargesExtra
  // use.
  useEffect(() => {
    if (!bailExtraFieldsReady) return;

    const bookingBailID = formData?.bookingBailID;
    if (!bookingBailID) {
      bailExtraIDRef.current = 0;
      setBailExtraFormValues(null);
      return;
    }

    fetchExtraTableRecord("BookingBailsExtra", "bookingBailID", bookingBailID)
      .then((result) => {
        if (!result) {
          bailExtraIDRef.current = 0;
          setBailExtraFormValues(null);
          return;
        }
        bailExtraIDRef.current = result.raw?.BookingBailsExtraID ?? 0;

        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = bailExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        setBailExtraFormValues(withDates);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  }, [formData?.bookingBailID, bailExtraFieldsReady]);

  // Report field keys/type map/current PK up to the parent whenever they
  // change — the parent needs these at save time, not render time.
  useEffect(() => {
    onExtraMetaChange?.({
      fieldKeys: bailExtraFields.map((f) => f.columnName),
      typeMap: bailExtraTypeMapRef.current,
      extraID: bailExtraIDRef.current,
    });
  }, [bailExtraFields, bailExtraFormValues]);

  useEffect(() => {
    setBailDetail(() => ({
      ...formData,
      offBondDate: isNonEmptyVariable(formData?.offBondDate)
        ? dayjs(formData?.offBondDate)
        : null,
    }));
  }, [formData]);

  useEffect(() => {
    form.setFieldsValue(bailDetail);
  }, [bailDetail]);

  // Pushed separately from the main bailDetail sync above so this fetch
  // resolving doesn't re-trigger that effect's other side effects.
  useEffect(() => {
    if (isNonEmptyVariable(bailExtraFormValues)) {
      form.setFieldsValue(bailExtraFormValues);
    }
  }, [bailExtraFormValues]);

  return (
    <FieldSecurityProvider
      entity={SCHEMA_NAME.BOOKING_BAILS}
      startingPoint={startingPoint}
      bookingsFieldsEnabled={bookingsFieldsEnabled}
    >
      <>
        <StyledSectionHeading>
          <span>Bail Information</span>
        </StyledSectionHeading>

        <Form form={form} initialValues={bailDetail}>
          <StyledGridRow gutter={16}>
            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="bailTypeID"
                formItemProps={{ label: "Bond Type", name: "bailTypeID" }}
              >
                <Select
                  options={getOptionsFromRefList(
                    "BondType",
                    false,
                    false,
                    true
                  )}
                  placeholder="Bond Type"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="bailAmount"
                formItemProps={{ label: "Amount", name: "bailAmount" }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="courtNumber"
                formItemProps={{ label: "Court #", name: "courtNumber" }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="offBondDate"
                formItemProps={{ label: "Off Bond Date", name: "offBondDate" }}
              >
                <CommonDatePicker
                  format="MM/DD/YYYY"
                  placeholder="MM/DD/YYYY"
                  showTime={false}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="requiredPaymentTypeID"
                formItemProps={{
                  name: "requiredPaymentTypeID",
                  label: "Required Payment Type",
                  layout: "vertical",
                }}
              >
                <Select
                  options={getOptionsFromRefList(
                    "Payment_Type",
                    false,
                    false,
                    true
                  )}
                  placeholder="Payment Type"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="citationType"
                formItemProps={{ label: "Citation Type", name: "citationType" }}
              >
                <Select />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={24}>
              <SecureFormItem
                fieldName="comments"
                formItemProps={{ label: "Comments", name: "comments" }}
              >
                <TextField inputType="textarea" />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>

        <StyledSectionHeading>
          <span>Charges for this bail</span>
        </StyledSectionHeading>

        <StyledSubTableContainer>
          <Table
            headers={[
              { key: "id", text: "#" },
              { key: "charge", text: "Charge" },
            ]}
            data={[]}
          />
        </StyledSubTableContainer>

        {/* BookingBailsExtra dynamic fields — PCI shows these right before
            "Bail Administrative Information" (see the matching heading
            just below). Shares the same `form` instance as the main Bail
            Information form above, in its own <Form> the way
            Property.jsx's two sections already do. */}
        {bailExtraFields.length > 0 && (
          <>
            <StyledSectionHeading marginTop="25px">
              <span>Additional Bail Information</span>
            </StyledSectionHeading>
            <Form form={form}>
              <StyledGridRow gutter={16}>
                {bailExtraFields.map((sf, index) => {
                  const view = hasCheckSecurity(
                    "Functionality", "field", "VIEW", "", "",
                    "BookingBailsExtra", sf.columnName, startingPoint,
                  );
                  const edit = hasCheckSecurity(
                    "Functionality", "field", "MODIFY", "", "",
                    "BookingBailsExtra", sf.columnName, startingPoint,
                  );
                  return (
                    <DynamicForm
                      key={index}
                      formAttributes={sf}
                      form={form}
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                      fullSearch={fullSearch}
                      view={view}
                      edit={edit}
                    />
                  );
                })}
              </StyledGridRow>
            </Form>
          </>
        )}

        <StyledSectionHeading marginTop="25px">
          <span>Bail Administrative Information</span>
        </StyledSectionHeading>

        <StyledGridRow gutter={16}>
          <StyledGridCol lg={4}>
            <SecureFormItem fieldName="id" formItemProps={{ label: "ID" }} overrideDisabled>
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          <StyledGridCol lg={6}>
            <SecureFormItem fieldName="dateEntered" formItemProps={{ label: "Date Entered" }} overrideDisabled>
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          <StyledGridCol lg={6}>
            <SecureFormItem fieldName="timeEntered" formItemProps={{ label: "Time Entered" }} overrideDisabled>
              <TextField />
            </SecureFormItem>
          </StyledGridCol>

          <StyledGridCol lg={6}>
            <SecureFormItem fieldName="user" formItemProps={{ label: "User" }} overrideDisabled>
              <TextField />
            </SecureFormItem>
          </StyledGridCol>
        </StyledGridRow>
      </>
    </FieldSecurityProvider>
  );
};

export default BailRecordDetailForm;